import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import { getLoginUserInfo, getRouters } from '@/services/uuc/auth';
import { getAccessToken, clearTokens } from '@/utils/auth';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * 应用全局初始状态
 * - currentUser：当前登录用户信息（API.LoginUserInfo）
 * - routers：后端下发的动态路由
 * - fetchUserInfo：手动刷新用户信息
 * - fetchRouters：手动刷新路由
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.LoginUserInfo;
  routers?: API.RouterItem[];
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.LoginUserInfo | undefined>;
  fetchRouters?: () => Promise<API.RouterItem[] | undefined>;
}> {
  // 拉取当前登录用户信息
  const fetchUserInfo = async () => {
    try {
      const resp = await getLoginUserInfo();
      if (resp?.code === 0 && resp.data) {
        return resp.data;
      }
      return undefined;
    } catch (_error) {
      return undefined;
    }
  };
  // 拉取动态路由
  const fetchRouters = async () => {
    try {
      const resp = await getRouters();
      if (resp?.code === 0 && resp.data) {
        return resp.data;
      }
      return undefined;
    } catch (_error) {
      return undefined;
    }
  };

  const { location } = history;
  // 登录页不拉取用户信息
  if (location.pathname !== loginPath) {
    // 没 Token 直接跳登录
    if (!getAccessToken()) {
      history.push(loginPath);
      return {
        fetchUserInfo,
        fetchRouters,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }
    const currentUser = await fetchUserInfo();
    if (!currentUser) {
      // Token 失效
      clearTokens();
      history.push(loginPath);
      return {
        fetchUserInfo,
        fetchRouters,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }
    // [变更] 后端无动态路由接口（/system/menu/routers），改走静态路由，避免每次进入后台都发一个 404 请求
    const routers: API.RouterItem[] = [];
    return {
      fetchUserInfo,
      fetchRouters,
      currentUser,
      routers,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    fetchRouters,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [<Question key="doc" />, <SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.nickname,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 没登录，重定向到 login
      if (
        !initialState?.currentUser &&
        location.pathname !== loginPath
      ) {
        history.push(loginPath);
      }
    },
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    childrenRender: (children) => {
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置
 * baseURL 留空，走 proxy 代理（/api/** -> http://localhost:8080/api/**）
 */
export const request: RequestConfig = {
  baseURL: '',
  ...errorConfig,
};
